import { VODAssetEntitlementState } from 'types/Entitlement';
import { Episode, Marker, Movie, Series, VodTerm } from 'types/Asset';
import { TimeCluster } from 'hooks/useBroadcastTimeCluster/useBroadcastTimeCluster';
import ApplicationConfig from 'providers/useConfig/ApplicationConfig';
import { LivePlayerAssetInfo } from 'types/Player';
import icons from 'style';
import translate from './fnTranslate';
import { DAYS, MILLISECONDS, MINUTES } from './TimeUnit';
import { isSeries } from './fnTypeGuards';
import { shouldOfferRental } from './fnEntitlement';

const getPrioContentMarker = (markers: Marker[]): Marker => {
    if (!markers?.length) return null;
    return [...markers].sort((a, b) => a.priority - b.priority)[0];
};

// TODO: this is not the right place... but where is? :D
const contentMarkers = {
    _DEFAULT: {
        color: '#ebebf1',
        background: '#0b50e5',
    },
    CONTENT_MARKER_NOW: {
        color: '#ebebf1',
        background: '#0b50e5',
        priority: 3,
    },
    CONTENT_MARKER_LIVE: {
        color: '#ebebf1',
        background: '#b8000d',
        priority: 2,
    },
    CONTENT_MARKER_NOT_SUBSCRIBED: {
        color: '#ebebf1',
        background: '#0b50e5',
        priority: 6,
    },
    CONTENT_MARKER_NEW: {
        color: '#ebebf1',
        background: '#0b50e5',
        priority: 1,
    },
    CONTENT_MARKER_EARLY_ACCESS: {
        color: '#000000',
        background: '#E79F36',
        priority: 0,
        icon: icons.cartIcon,
    },
    CONTENT_MARKER_FREE: {
        color: '#ebebf1',
        background: '#0b50e5',
        priority: 1,
        transparency: 1,
    },
    CONTENT_MARKER_NEW_EPISODES: {
        color: '#ebebf1',
        background: '#0b50e5',
        priority: 0,
    },
    RECORDING_IN_PROGRESS: {
        color: '#ebebf1',
        background: '#B8000D',
        priority: 1,
    },
    RECORDING_COMPLETED: {
        color: '#ebebf1',
        background: '#B8000D',
        priority: 1,
    },
};

const getMarker = (markerKey: string, rentalTime?: number, price?: number): Marker => {
    // eslint-disable-next-line no-underscore-dangle
    const marker = contentMarkers[markerKey] ?? contentMarkers._DEFAULT;
    let value: string;

    if (rentalTime) {
        if (markerKey === 'CONTENT_MARKER_REMAINING_HOURS') {
            value = translate(markerKey, { HOURS: `${rentalTime}` });
        }
        if (markerKey === 'CONTENT_MARKER_REMAINING_MIN') {
            value = translate(markerKey, { MINUTES: `${rentalTime}` });
        }
    } else if (price !== undefined && markerKey === 'CONTENT_MARKER_EARLY_ACCESS') {
        value = translate(markerKey, { PRICE: `${price} €` });
    } else {
        value = translate(markerKey);
    }

    return {
        value,
        backgroundColor: marker.background || ApplicationConfig.layout_config.primary,
        textColor: marker.color,
        priority: marker.priority,
        icon: marker.icon,
    };
};

export const getEpgModalContentMarker = (isRecorded: boolean, timeCluster: TimeCluster, recordedTimeCluster?: TimeCluster): Marker => {
    const markers: Marker[] = [];

    const effectiveTimeCluster = recordedTimeCluster || timeCluster;

    if (isRecorded && (effectiveTimeCluster === 'LIVE' || effectiveTimeCluster === 'FUTURE')) {
        markers.push(getMarker('RECORDING_IN_PROGRESS'));
    }

    if (isRecorded && effectiveTimeCluster === 'PAST') {
        markers.push(getMarker('RECORDING_COMPLETED'));
    }

    return getPrioContentMarker(markers);
};

export const getBroadcastCardContentMarkerValue = (isLiveEvent: boolean, timeCluster: TimeCluster): Marker => {
    const markers: Marker[] = [];

    if (isLiveEvent && timeCluster === 'LIVE') {
        markers.push(getMarker('CONTENT_MARKER_LIVE'));
    }

    if (timeCluster === 'LIVE') {
        markers.push(getMarker('CONTENT_MARKER_NOW'));
    }
    if (timeCluster === 'FUTURE') {
        markers.push(getMarker('CONTENT_MARKER_UPCOMING'));
    }

    return getPrioContentMarker(markers);
};

export const getNowAndNextCardContentMarkerValue = (isLiveEvent: boolean): Marker => {
    if (isLiveEvent) {
        return getMarker('CONTENT_MARKER_LIVE');
    }

    return null;
};

export const getSeriesCardContentMarkerValue = (
    firstAvailability: number,
    isVOD: boolean,
    lastEpisodeFirstAvailability: number
): Marker => {
    const now = new Date().getTime();
    const markers: Marker[] = [];

    if (firstAvailability !== -1 && now - firstAvailability < DAYS.toMillis(14)) {
        markers.push(getMarker('CONTENT_MARKER_NEW'));
    }

    if (isVOD && lastEpisodeFirstAvailability !== -1 && now - lastEpisodeFirstAvailability < DAYS.toMillis(14)) {
        markers.push(getMarker('CONTENT_MARKER_NEW_EPISODES'));
    }

    return getPrioContentMarker(markers);
};

export const getVodContentMarker = (
    price: number,
    entitlementState?: string,
    vod_session_until?: number,
    vodTerm?: VodTerm,
    hasPurchased = false
): Marker => {
    const now = new Date().getTime();
    const markers: Marker[] = [];

    const vodSessionUntilMillis = vod_session_until ? vod_session_until * 1000 : undefined;
    const isEntitled = entitlementState === VODAssetEntitlementState.ENTITLED;
    const hasActiveVodSession = isEntitled && !!vodSessionUntilMillis && vodSessionUntilMillis > now;

    if (hasActiveVodSession) {
        const remainingTime = vodSessionUntilMillis - now;

        const maxRemainingTime = MINUTES.toMillis(60 * 48);
        if (remainingTime <= maxRemainingTime) {
            if (MILLISECONDS.toMinutes(remainingTime) >= 1) {
                if (remainingTime < MINUTES.toMillis(60)) {
                    markers.push(getMarker('CONTENT_MARKER_REMAINING_MIN', Math.floor(MILLISECONDS.toMinutes(remainingTime))));
                } else {
                    markers.push(getMarker('CONTENT_MARKER_REMAINING_HOURS', Math.floor(MILLISECONDS.toHours(remainingTime))));
                }
            }
        }
    }

    if (!isEntitled && !hasPurchased && shouldOfferRental(vodTerm, vod_session_until) && price != null) {
        markers.push(getMarker('CONTENT_MARKER_EARLY_ACCESS', undefined, price));
    }

    return getPrioContentMarker(markers);
};

export const getProgramDetailContentMarker = (timeCluster: TimeCluster, isLiveEvent: boolean): Marker => {
    const markers: Marker[] = [];

    if (isLiveEvent && timeCluster === 'LIVE') {
        markers.push(getMarker('CONTENT_MARKER_LIVE'));
    }

    if (timeCluster === 'LIVE') {
        markers.push(getMarker('CONTENT_MARKER_NOW'));
    }
    if (timeCluster === 'FUTURE') {
        markers.push(getMarker('CONTENT_MARKER_UPCOMING'));
    }

    return getPrioContentMarker(markers);
};

export const getTVSeriesDetailContentMarker = (timeCluster: TimeCluster, isLiveEvent?: boolean): Marker => {
    const markers: Marker[] = [];

    if (isLiveEvent && timeCluster === 'LIVE') {
        markers.push(getMarker('CONTENT_MARKER_LIVE'));
    }

    if (timeCluster === 'LIVE') {
        markers.push(getMarker('CONTENT_MARKER_NOW'));
    }
    if (timeCluster === 'FUTURE') {
        markers.push(getMarker('CONTENT_MARKER_UPCOMING'));
    }

    return getPrioContentMarker(markers);
};

export const getGalleryVODContentMarker = (asset: Movie | Series | Episode): Marker => {
    const markers: Marker[] = [];

    const { firstAvailability } = asset;
    const now = new Date().getTime();

    if (firstAvailability !== -1 && now - firstAvailability < DAYS.toMillis(14)) {
        markers.push(getMarker('CONTENT_MARKER_NEW'));
    }

    if (isSeries(asset) && asset.lastEpisodeFirstAvailability !== -1 && now - asset.lastEpisodeFirstAvailability < DAYS.toMillis(14)) {
        markers.push(getMarker('CONTENT_MARKER_NEW_EPISODES'));
    }

    return getPrioContentMarker(markers);
};

export const getPlayerEpisodeContentMarker = (isPlaying: boolean, requiresSubscription: boolean, isUpcoming: boolean): Marker => {
    if (isUpcoming) {
        return getMarker('CONTENT_MARKER_UPCOMING');
    }

    if (requiresSubscription) {
        return getMarker('CONTENT_MARKER_NOT_SUBSCRIBED');
    }

    return isPlaying ? getMarker('CONTENT_MARKER_NOW_PLAYING') : null;
};

export const getLivePlayerAssetContentMarker = (asset: LivePlayerAssetInfo, timeCluster: TimeCluster): Marker => {
    const markers: Marker[] = [];

    if (!asset) return null;

    if (asset.isLive && timeCluster === 'LIVE') {
        markers.push(getMarker('CONTENT_MARKER_LIVE'));
    }

    if (timeCluster === 'LIVE') {
        markers.push(getMarker('CONTENT_MARKER_NOW'));
    }

    return getPrioContentMarker(markers);
};
